import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import FloatingDock from "./components/FloatingDock";
import Index from "./pages/Index";
import Chat from "./pages/Chat";
import Journal from "./pages/Journal";
import SafetyPlan from "./pages/SafetyPlan";
import Resources from "./pages/Resources";
import Login from "./pages/Login";
import Profile from "./pages/Profile";
import Subscription from "./pages/Subscription";
import { useToast } from "@/hooks/use-toast";

// Protected Route component
const ProtectedRoute = ({ 
  children, 
  requiresSubscription = false 
}: { 
  children: React.ReactNode;
  requiresSubscription?: boolean;
}) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasSubscription, setHasSubscription] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    // Initialize auth state
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session && requiresSubscription) {
        checkSubscription(session);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      if (session && requiresSubscription) {
        await checkSubscription(session);
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [requiresSubscription]);

  const checkSubscription = async (session: Session) => {
    try {
      console.log('Checking subscription for user:', session.user.email);
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) {
        console.error('Subscription check error:', error);
        toast({
          title: "Error checking subscription",
          description: "Please try again later",
          variant: "destructive",
        });
        setLoading(false);
        return;
      }

      console.log('Subscription check response:', data);
      setHasSubscription(data.subscribed);
      
      if (!data.subscribed) {
        toast({
          title: "Subscription Required",
          description: "Please subscribe to access this feature",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Subscription check error:', error);
      toast({
        title: "Error checking subscription",
        description: "Please try again later",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!session) {
    return <Navigate to="/login" />;
  }

  if (requiresSubscription && !hasSubscription) {
    return <Navigate to="/subscription" />;
  }

  return <>{children}</>;
};

function App() {
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    // Initialize auth state
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  return (
    <Router>
      <div className="min-h-screen bg-background">
        {window.location.pathname !== "/login" && <FloatingDock />}
        <Routes>
          {/* Public routes */}
          <Route path="/" element={<Index />} />
          <Route path="/login" element={<Login />} />
          <Route path="/resources" element={<Resources />} />
          
          {/* Protected routes requiring subscription */}
          <Route
            path="/chat"
            element={
              <ProtectedRoute requiresSubscription>
                <Chat />
              </ProtectedRoute>
            }
          />
          <Route
            path="/journal"
            element={
              <ProtectedRoute requiresSubscription>
                <Journal />
              </ProtectedRoute>
            }
          />
          <Route
            path="/safety-plan"
            element={
              <ProtectedRoute requiresSubscription>
                <SafetyPlan />
              </ProtectedRoute>
            }
          />

          {/* Protected routes not requiring subscription */}
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscription"
            element={
              <ProtectedRoute>
                <Subscription />
              </ProtectedRoute>
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;